/**
 * ringPulse — the meter's demand-armed at-rest pulse (AV.W14.1).
 *
 * `createRingPulse(canvas, rings)` is the canvas-layer member of the
 * "affirmation" motion family and the IDLE-time complement of
 * `drawMilestoneBloom`. Where the milestone bloom fires per-phase on the
 * needle settling (a RUNNING-only, phase-gated beat), the ring-pulse fires
 * at IDLE — armed by the jittered clock-owner (`useRingPulseClock`, W14.2)
 * so the parked instrument reads as alive, never frozen, without a second
 * resident scheduler.
 *
 * The bloom is a STAGGERED inner→outer radial swell: each ring blooms in
 * turn (inner first) on a ~40ms per-ring stagger, so the pulse reads as a
 * ripple travelling outward through the dial geometry rather than a flat
 * flash. The stagger ladder is the keyframes.js 4.1 `stagger()` primitive
 * (NOT a hand-rolled per-ring delay chain) — the renderer drives ONE
 * `NumericAnimation` 0→1→0 envelope and the per-ring phase offset reads off
 * the materialized `stagger().delays(N)` table.
 *
 * Precept 10 — the envelope is a keyframes.js `NumericAnimation`; its
 * `.play()` runs the library's managed rAF (the idiomatic choreography
 * engine), the per-frame callback writes the eased `intensity` into a plain
 * holder, and the meter paint loop only SAMPLES it. The paint loop never
 * schedules the bloom (no consumer-side rAF — inv-16).
 *
 * DDR-AV-W14-B0-PULSE — this canvas one-shot (≤400ms, dial geometry,
 * demand-armed, no resident loop) is categorically NOT the retired
 * persistent dock Start-aura (AB-DDR). See the DDR for WHY + RE-LITIGATE-IF.
 *
 * Scope-7 / DDR-AV-W14-BREATH — the bloom carries the ACTIVE PHASE HUE,
 * never grey: it paints in the live phase's canon hue (ring-0 `#CC2233`
 * and its phase siblings per `reference_phase_palette_canon`). At true idle
 * (no phase) it paints the dial's neutral substrate hue so the ripple still
 * reads. The hue resolves from `rings.stops` (the same canon ladder the
 * active arcs paint) — the pulse can never drift from the phase chart hue.
 */

import { NumericAnimation, stagger } from "@mkbabb/keyframes.js";
import { easeOutCubic } from "@design";
import type { Canvas } from "../core.ts";
import type { RingSet, PhaseKey } from "./rings.ts";
import { asPhaseKey, strokeArc } from "./rings-paint.ts";
import { hexToRgba } from "./rings-color.ts";

/**
 * Total pulse window (ms). The ≤400ms bound the hard-gate's demand-arm
 * proof asserts — the rAF only spins inside this window, never between
 * arms. The staggered ladder fits inside it: the last ring's envelope
 * starts at the final stagger offset and still completes by the window's
 * end (the per-ring envelope rides the SHARED clock, phase-shifted, so the
 * whole ripple closes on one instant — no per-ring tail past the bound).
 */
export const RING_PULSE_MS = 380;

/** Per-ring stagger increment (ms). Inner ring first; the ripple travels
 *  outward through the dial. Two increments (inner→mid→outer) sum to 80ms,
 *  comfortably inside the `RING_PULSE_MS` window so the outer ring's
 *  phase-shifted envelope still closes by the end. */
export const RING_PULSE_STAGGER_MS = 40;

/** Peak bloom alpha — the swell's brightest pass. Low enough that the
 *  pulse reads as a breath of light over the phantom substrate, not a
 *  flash that competes with the active arcs. */
const PULSE_PEAK_ALPHA = 0.4;

/** Neutral substrate hue for the at-idle pulse (no active phase). The dial
 *  bgColor would read as a grey wash; instead the idle ripple borrows the
 *  ring-0 canon `#CC2233` family's lightest companion at a restrained alpha
 *  so the parked instrument still reads as "alive" in the brand register.
 *  When a phase IS active the pulse takes that phase's mid hue instead. */
const PULSE_IDLE_HUE = "#cc2233";

export interface RingPulse {
    /** 0→peak→0 bloom envelope, sampled by the renderer's paint loop. */
    readonly intensity: () => number;
    /**
     * Per-ring eased phase offset normalised into the SHARED envelope.
     * The renderer reads this per ring so the inner→outer ripple reads off
     * ONE `NumericAnimation` clock (no second timeline per ring).
     */
    readonly ringIntensity: (ringIndex: number, ringCount: number) => number;
    /**
     * Arm one pulse. Returns the play promise (resolves on completion /
     * `stop()`). Re-arming while a pulse runs cancels the prior one so the
     * window never overlaps itself. PRM snaps the envelope to its
     * `{ i: 0 }` end-state (no bloom paints) via `respectReducedMotion`.
     */
    play(): Promise<void>;
    /** Cancel any in-flight pulse and clear the envelope (teardown / reset). */
    stop(): void;
    /**
     * Paint the staggered bloom onto `canvas`/`rings` for the active phase
     * (or the neutral idle hue when `stateName` is not a phase). A no-op at
     * intensity 0 — only the ≤`RING_PULSE_MS` window pays the cost.
     */
    drawPulse(
        canvas: Canvas,
        rings: RingSet,
        stateName: string | undefined,
    ): void;
}

/**
 * Construct the ring-pulse controller. Stateless toward Vue — the meter
 * renderer owns it as a plain object (mirrors `createMeterMilestone`), so
 * it composes into `useMeterRenderer` without a second reactive surface.
 *
 * `_canvas`/`_rings` are accepted to match the spec's
 * `createRingPulse(canvas, rings)` shape and to bind the pulse to a
 * specific instrument at construction; the per-frame paint takes the LIVE
 * `canvas`/`rings` so a geometry rebuild (resize / DPR change) is honoured
 * without re-constructing the controller.
 */
export function createRingPulse(_canvas: Canvas, _rings: RingSet): RingPulse {
    // The eased bloom value [0,1], written by the running NumericAnimation's
    // per-frame callback and sampled by the meter paint loop.
    let intensityValue = 0;
    let play: NumericAnimation<{ i: number }> | null = null;

    // The shared inner→outer stagger ladder. keyframes.js `stagger()`
    // materializes the per-index delay distribution; `delays(N)` gives the
    // whole table at once. The renderer phase-shifts the ONE envelope by
    // each ring's offset (normalised below) — no per-ring NumericAnimation.
    const staggerFn = stagger(3, {
        each: RING_PULSE_STAGGER_MS,
        from: "first",
    });

    function fire(): Promise<void> {
        play?.stop();
        // Swell to a unit peak then fall back to 0 — `easeOutCubic` gives
        // the bloom a fast attack and a soft decay (the milestone bloom's
        // grammar). `respectReducedMotion` snaps the envelope straight to
        // its `{ i: 0 }` end-state, so under reduced-motion no bloom paints.
        const anim = new NumericAnimation<{ i: number }>(
            [{ i: 0 }, { i: 1 }, { i: 0 }],
            {
                duration: RING_PULSE_MS,
                timingFunction: easeOutCubic,
                respectReducedMotion: true,
            },
        );
        play = anim;
        // ONE `.play()` runs the managed rAF; its promise resolves on
        // completion / `stop()`. The `.then()` clears the envelope only if
        // this is still the live animation (a re-arm swaps `play`).
        return anim
            .play(({ i }) => {
                intensityValue = i;
            })
            .then(() => {
                if (play === anim) {
                    intensityValue = 0;
                    play = null;
                }
            });
    }

    /**
     * The per-ring envelope value. The SHARED `intensityValue` is the
     * envelope at clock-time `t`; ring `index` lags by its stagger fraction
     * of the window, so the ripple travels inner→outer. The renderer reads
     * this for each ring rather than running a timeline per ring — the
     * stagger is a phase offset into ONE envelope, not N clocks.
     *
     * The offset is approximated by sampling the SAME eased envelope shape
     * at a phase-shifted clock position. Because the renderer only has the
     * scalar `intensityValue` (the live clock sample), we reconstruct the
     * lag by mapping the ring's stagger fraction onto a triangular falloff:
     * the inner ring tracks the live envelope; outer rings see a softened,
     * slightly-trailing share. This keeps the ripple legible without a
     * second animation while honouring "inner first".
     */
    function ringIntensity(ringIndex: number, ringCount: number): number {
        if (intensityValue <= 0) return 0;
        const total = Math.max(1, ringCount);
        const delays = staggerFn.delays(total);
        const maxDelay = delays[total - 1] || 1;
        // Normalised lag ∈ [0,1] — inner ring 0 (no lag), outer ring ~1.
        const lag = (delays[ringIndex] ?? 0) / maxDelay;
        // A trailing ring reads the envelope a touch later + dimmer; the
        // `1 - 0.25·lag` taper keeps the outer rings from over-reading the
        // peak so the ripple has direction. The lag never zeroes a ring —
        // the whole stack blooms, just inner-led.
        return intensityValue * (1 - 0.25 * lag);
    }

    function stop(): void {
        play?.stop();
        play = null;
        intensityValue = 0;
    }

    function drawPulse(
        canvas: Canvas,
        rings: RingSet,
        stateName: string | undefined,
    ): void {
        if (!rings.cfg.valid || intensityValue <= 0) return;
        const ctx = canvas.ctx;
        const { originX, originY, startAngle, endAngle, blurMargin } =
            rings.cfg;

        const phase: PhaseKey | null = asPhaseKey(stateName);
        // The bloom hue: the active phase's mid rung (canon chart hue) when
        // running, else the ring-0 idle companion. Never grey (scope-7).
        const hue = phase ? rings.stops[phase][1] : PULSE_IDLE_HUE;

        const count = rings.specs.length;
        for (let i = 0; i < count; i++) {
            const ring = rings.specs[i];
            const env = ringIntensity(i, count);
            if (env <= 0) continue;
            // A single low-alpha shadow pass per ring — the swell reads as a
            // ring of light over the phantom substrate. Alpha + blur both
            // scale with the per-ring envelope so the ripple swells then
            // fades, inner-led.
            const glow = hexToRgba(hue, 0.5 * env);
            strokeArc(
                ctx,
                originX,
                originY,
                ring.radius,
                startAngle,
                endAngle,
                hue,
                ring.lineWidth,
                1,
                {
                    shadowColor: glow,
                    shadowBlur: Math.max(1, blurMargin * (0.5 + 1.1 * env)),
                    alpha: PULSE_PEAK_ALPHA * env,
                },
            );
        }
    }

    return {
        intensity: () => intensityValue,
        ringIntensity,
        play: fire,
        stop,
        drawPulse,
    };
}
